/**
 * @fileoverview Merge de listado inbox: API Mercately y/o inbox MS (servicio externo).
 * Algunos canales del sidebar solo existen en inbox MS; otros solo en Mercately; sin filtro de canal, ambas fuentes.
 *
 * @module dualApiMergeService
 * @category Service - Inbox
 * @since 3.1.0
 */
import ConversationModelMC from '../../presentation/models/inbox/ConversationModelMC';
import {
  isInboxMsChatPlatform,
  isInboxMsMigratingPlatform,
  resolveMsApiPlatforms
} from './inboxMsChatPlatform';

/** @param {Object} params */
function isInboxMsExclusiveFilter(params) {
  const { platform } = params;
  if (!platform) return false;
  return isInboxMsChatPlatform(platform);
}

/**
 * Canal cuyo listado, mientras dura la migración, vive repartido entre las dos
 * fuentes: el MS tiene lo que el seed ya copió y Rails conserva el resto.
 *
 * Es un subconjunto estricto de `isInboxMsExclusiveFilter` —lo exige como
 * precondición— y las dos cadenas `if/else` del orquestador lo evalúan ANTES que a
 * él. Ese orden es la única razón por la que no hace falta tocar los otros dos
 * predicados: `isMercatelyOnlyChannelFilter` está definido como "hay canal y no es
 * del MS", así que aflojar `isInboxMsExclusiveFilter` haría que esa rama se tragara
 * el caso y pidiera solo a Rails, que es peor que el hueco que esto arregla.
 *
 * `msPlatforms.length > 1` lo restringe al paraguas WhatsApp, que es exactamente el
 * valor con el que compara `should_include_whatsapp?` en Rails.
 *
 * @param {Object} params
 * @param {string[]} msPlatforms
 */
function isDualMigratingChannelFilter(params, msPlatforms) {
  if (!isInboxMsExclusiveFilter(params)) return false;
  if (!isInboxMsMigratingPlatform(params.platform)) return false;
  // Rails no tiene `platform_retailers`: filtrar por cuenta concreta no tiene fuente
  // que sumar, así que ahí se sigue pidiendo solo al MS.
  if (params.platform_retailer_id) return false;
  return msPlatforms.length > 1;
}

/** Canal explícito que solo debe resolverse en Rails (no migrado a inbox MS). */
function isMercatelyOnlyChannelFilter(params) {
  const { platform } = params;
  if (!platform) return false;
  if (isInboxMsExclusiveFilter(params)) return false;
  return true;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getChatDate(chat) {
  const attrs = chat.attributes || chat;
  const dateStr = attrs.interaction_date || attrs.recent_message_date || 0;
  return new Date(dateStr).getTime();
}

// ─── Offsets ─────────────────────────────────────────────────────────────────

/**
 * @returns {{ mercatelyOffset: number, inboxMsOffset: number, offsetsByPlatform: Object.<string, number> }}
 */
function calculateOffsets(page, previousPagination) {
  const isFirstPage = page === 1;
  const prevInboxMs = previousPagination?.inboxMsOffset
    ?? previousPagination?.microserviceInboxOffset
    ?? previousPagination?.tiktokOffset;
  return {
    mercatelyOffset: isFirstPage ? 0 : (previousPagination?.mercatelyOffset || 0),
    inboxMsOffset: isFirstPage ? 0 : (prevInboxMs || 0),
    offsetsByPlatform: isFirstPage ? {} : (previousPagination?.offsetsByPlatform || {})
  };
}

/**
 * Avanza un offset por subtype del MS, uno por cada chat suyo que de verdad entró en
 * la página. Los chats de Rails no están en el mapa y no mueven nada.
 *
 * @returns {Object.<string, number>}
 */
function advancePlatformOffsets(previousOffsets, topChats, msPlatformByChat) {
  const nextOffsets = { ...previousOffsets };
  topChats.forEach((chat) => {
    const platform = msPlatformByChat.get(chat);
    if (!platform) return;
    nextOffsets[platform] = (nextOffsets[platform] || 0) + 1;
  });
  return nextOffsets;
}

// ─── Fetch ───────────────────────────────────────────────────────────────────

const emptyInboxResponse = () => ({ data: [], pagination: { has_next_page: false } });

async function fetchMercatelyAndInboxMs(mercatelyParams, inboxMsParams) {
  return Promise.all([
    ConversationModelMC.getConversations(mercatelyParams).catch((err) => {
      // eslint-disable-next-line no-console
      console.error('Error fetching Mercately inbox chats', err);
      return emptyInboxResponse();
    }),
    ConversationModelMC.getMicroserviceConversations(inboxMsParams).catch((err) => {
      // eslint-disable-next-line no-console
      console.error('Error fetching inbox MS chats', err);
      return emptyInboxResponse();
    })
  ]);
}

async function fetchInboxMsOnly(inboxMsParams) {
  const inboxMsResponse = await ConversationModelMC.getMicroserviceConversations(inboxMsParams).catch((err) => {
    // eslint-disable-next-line no-console
    console.error('Error fetching inbox MS chats', err);
    return emptyInboxResponse();
  });
  return [emptyInboxResponse(), inboxMsResponse];
}

/**
 * El MS solo acepta un `platform` del enum (@IsIn). Para umbrellas (WhatsApp
 * BSP/QR/Gupshup) sin cuenta concreta, pedimos cada subtype con su propio offset
 * y mergeamos con cursores independientes.
 *
 * NO avanza ningún offset: devuelve el mapa chat→subtype y deja que el orquestador
 * los avance sobre `topChats`, ya mergeados con Rails. Comprometerlos aquí solo es
 * correcto si nada descarta chats después, y en la rama dual `mergeAndSort` sí lo
 * hace: los chats del MS que se caen del top-25 global tendrían su offset quemado
 * sin haberse pintado, y no se volverían a pedir nunca.
 *
 * El truncado a `perPage` de aquí sí es seguro: un chat descartado en este punto es
 * más viejo que otros `perPage` del propio MS, luego no cabía en la página global.
 */
async function fetchInboxMsMultiPlatform(
  baseParams,
  platforms,
  perPage,
  isAsc,
  offsetsByPlatform = {}
) {
  const responses = await Promise.all(
    platforms.map((platform) => ConversationModelMC.getMicroserviceConversations({
      ...baseParams,
      platform,
      offset: offsetsByPlatform[platform] || 0
    }).catch((err) => {
      // eslint-disable-next-line no-console
      console.error(`Error fetching inbox MS chats (${platform})`, err);
      return emptyInboxResponse();
    }))
  );

  const tagged = responses.flatMap((response, index) => (
    (response?.data || []).map((chat) => ({ chat, platform: platforms[index] }))
  ));

  const sorted = [...tagged].sort((a, b) => {
    const dateA = getChatDate(a.chat);
    const dateB = getChatDate(b.chat);
    return isAsc ? dateA - dateB : dateB - dateA;
  });

  const page = sorted.slice(0, perPage);
  const msPlatformByChat = new Map(page.map(({ chat, platform }) => [chat, platform]));

  const hasUnused = sorted.length > perPage;
  const hasNextPage = hasUnused
    || responses.some((response) => !!response?.pagination?.has_next_page);

  return {
    inboxMsResponse: {
      data: page.map(({ chat }) => chat),
      pagination: { has_next_page: hasNextPage }
    },
    msPlatformByChat
  };
}

async function fetchMercatelyInboxOnly(mercatelyParams) {
  const mercatelyResponse = await ConversationModelMC.getConversations(mercatelyParams).catch((err) => {
    // eslint-disable-next-line no-console
    console.error('Error fetching Mercately inbox chats', err);
    return emptyInboxResponse();
  });
  return [mercatelyResponse, emptyInboxResponse()];
}

// ─── Merge / paginación ──────────────────────────────────────────────────────

function mergeAndSort(mercatelyChats, inboxMsChats, perPage, isAsc) {
  const combined = [...mercatelyChats, ...inboxMsChats].sort((a, b) => {
    const dateA = getChatDate(a);
    const dateB = getChatDate(b);
    return isAsc ? dateA - dateB : dateB - dateA;
  });
  return combined.slice(0, perPage);
}

/**
 * How many page chats came from each source, so their offset can advance.
 *
 * Counted by object identity (`mergeAndSort` returns the same references
 * that arrived), not by `platform`: that key does not distinguish the origin.
 * `facebook_comments` and `instagram_comments` are literally the same string in
 * both APIs, and the rollout flag responds equally for the Rails `whatsapp`
 * as for the MS `whatsapp_bsp_messages`. Misclassifying here misaligns the
 * two offsets: one source repeats a page and the other skips chats.
 *
 * @returns {{ usedMercately: number, usedInboxMs: number }}
 */
function countChatsByListSource(topChats, inboxMsChats) {
  const fromInboxMs = new Set(inboxMsChats);
  let usedMercately = 0;
  let usedInboxMs = 0;
  topChats.forEach((chat) => {
    if (fromInboxMs.has(chat)) {
      usedInboxMs += 1;
    } else {
      usedMercately += 1;
    }
  });
  return { usedMercately, usedInboxMs };
}

function buildCombinedPagination({
  currentPage,
  perPage,
  mercatelyHasNextPage,
  inboxMsHasNextPage,
  mercatelyChatsFetched,
  inboxMsChatsFetched,
  usedMercately,
  usedInboxMs,
  nextMercatelyOffset,
  nextInboxMsOffset,
  offsetsByPlatform
}) {
  const hasUnusedMercately = usedMercately < mercatelyChatsFetched;
  const hasUnusedInboxMs = usedInboxMs < inboxMsChatsFetched;
  const hasNextPage = mercatelyHasNextPage || inboxMsHasNextPage || hasUnusedMercately || hasUnusedInboxMs;

  return {
    current_page: currentPage,
    per_page: perPage,
    has_next_page: hasNextPage,
    has_previous_page: currentPage > 1,
    mercatelyOffset: nextMercatelyOffset,
    inboxMsOffset: nextInboxMsOffset,
    offsetsByPlatform: offsetsByPlatform || {}
  };
}

// ─── Público ─────────────────────────────────────────────────────────────────

// eslint-disable-next-line import/prefer-default-export
export async function fetchAndMergeDualApiChats(params, previousPagination) {
  const perPage = params.per_page || 25;
  const currentPage = params.page || 1;
  const isAsc = (params.order || '').endsWith('_asc');

  const { mercatelyOffset, inboxMsOffset, offsetsByPlatform } = calculateOffsets(
    currentPage,
    previousPagination
  );

  const mercatelyParams = {
    ...params, page: 1, offset: mercatelyOffset, per_page: perPage
  };
  const { platform: _rawPlatform, ...paramsForMs } = params;
  const inboxMsParams = {
    ...paramsForMs,
    page: 1,
    offset: inboxMsOffset,
    per_page: perPage
  };
  const msPlatforms = resolveMsApiPlatforms(params.platform);
  // El MS valida `platform` con @IsIn (un solo enum). Nunca enviar lista CSV.
  if (msPlatforms.length === 1) {
    [inboxMsParams.platform] = msPlatforms;
  }

  let mercatelyResponse;
  let inboxMsResponse;
  // Distinto de `{}`: ser no-nulo es lo que marca "estoy en modo multiplataforma",
  // que es lo que decide qué cursor del MS avanza. Ver `nextInboxMsOffset`.
  let msPlatformByChat = null;

  // La rama dual va primera: `isInboxMsExclusiveFilter` también es cierto para ella.
  const isDualMigrating = isDualMigratingChannelFilter(params, msPlatforms);

  if (isDualMigrating) {
    const [mercately, multi] = await Promise.all([
      ConversationModelMC.getConversations(mercatelyParams).catch((err) => {
        // eslint-disable-next-line no-console
        console.error('Error fetching Mercately inbox chats', err);
        return emptyInboxResponse();
      }),
      fetchInboxMsMultiPlatform(inboxMsParams, msPlatforms, perPage, isAsc, offsetsByPlatform)
    ]);
    mercatelyResponse = mercately;
    inboxMsResponse = multi.inboxMsResponse;
    msPlatformByChat = multi.msPlatformByChat;
  } else if (isInboxMsExclusiveFilter(params) && msPlatforms.length > 1) {
    if (inboxMsParams.platform_retailer_id) {
      // La cuenta ya acota el query; no hace falta platform.
      [mercatelyResponse, inboxMsResponse] = await fetchInboxMsOnly(inboxMsParams);
    } else {
      const multi = await fetchInboxMsMultiPlatform(
        inboxMsParams,
        msPlatforms,
        perPage,
        isAsc,
        offsetsByPlatform
      );
      mercatelyResponse = emptyInboxResponse();
      inboxMsResponse = multi.inboxMsResponse;
      msPlatformByChat = multi.msPlatformByChat;
    }
  } else if (isInboxMsExclusiveFilter(params)) {
    [mercatelyResponse, inboxMsResponse] = await fetchInboxMsOnly(inboxMsParams);
  } else if (isMercatelyOnlyChannelFilter(params)) {
    [mercatelyResponse, inboxMsResponse] = await fetchMercatelyInboxOnly(mercatelyParams);
  } else {
    [mercatelyResponse, inboxMsResponse] = await fetchMercatelyAndInboxMs(mercatelyParams, inboxMsParams);
  }

  const mercatelyChats = mercatelyResponse?.data || [];
  const inboxMsChats = inboxMsResponse?.data || [];

  const topChats = mergeAndSort(mercatelyChats, inboxMsChats, perPage, isAsc);

  let usedMercately;
  let usedInboxMs;
  if (isDualMigrating) {
    // Mismo orden que arriba y por la misma razón: aquí las dos fuentes aportan, así
    // que forzar `usedMercately = 0` congelaría el offset de Rails en 0 y repetiría
    // su primera página indefinidamente.
    ({ usedMercately, usedInboxMs } = countChatsByListSource(topChats, inboxMsChats));
  } else if (isInboxMsExclusiveFilter(params)) {
    usedMercately = 0;
    usedInboxMs = topChats.length;
  } else if (isMercatelyOnlyChannelFilter(params)) {
    usedMercately = topChats.length;
    usedInboxMs = 0;
  } else {
    ({ usedMercately, usedInboxMs } = countChatsByListSource(topChats, inboxMsChats));
  }

  // En modo multiplataforma el cursor del MS son los offsets por subtype, y se
  // avanzan sobre la página ya mergeada. Fuera de él se limpia el hash: heredar uno
  // no vacío congelaba `inboxMsOffset` y el MS devolvía la misma página en cada
  // scroll (p. ej. al sellarse la migración entre dos páginas).
  const nextOffsetsByPlatform = msPlatformByChat
    ? advancePlatformOffsets(offsetsByPlatform, topChats, msPlatformByChat)
    : {};

  const pagination = buildCombinedPagination({
    currentPage,
    perPage,
    mercatelyHasNextPage: !!mercatelyResponse?.pagination?.has_next_page,
    inboxMsHasNextPage: !!inboxMsResponse?.pagination?.has_next_page,
    mercatelyChatsFetched: mercatelyChats.length,
    inboxMsChatsFetched: inboxMsChats.length,
    usedMercately,
    usedInboxMs,
    nextMercatelyOffset: mercatelyOffset + usedMercately,
    nextInboxMsOffset: msPlatformByChat ? inboxMsOffset : inboxMsOffset + usedInboxMs,
    offsetsByPlatform: nextOffsetsByPlatform
  });

  return { data: topChats, pagination };
}
